import { MORPHS, type Morph } from "./axolotl_morphs";

// Procedural drawings of every Lippie form. Each form draws in a unit space where the ground is y = 0,
// the creature faces the viewer (slightly to the left) and is about 100 units tall.

const TAU = Math.PI * 2;

export type Face = "normal" | "happy" | "ouch" | "sleepy";

export interface LippieOpts {
  t?: number;
  face?: Face;
  // Mirror so the Lippie faces right (used for the player's side in battle).
  flip?: boolean;
  // Paint every shape in one color: silhouettes in the Journal, white flash when evolving.
  mono?: string;
  // Palette override for the axolotl forms (e.g. the Midnight reward).
  morph?: Morph;
}

// ---------- paint helpers ----------

let mono: string | null = null;

const col = (c: string) => mono ?? c;

function ink(ctx: CanvasRenderingContext2D, fill: string, line: string, lw = 2.6) {
  ctx.fillStyle = col(fill);
  ctx.fill();
  ctx.strokeStyle = col(line);
  ctx.lineWidth = lw;
  ctx.stroke();
}

function fillOnly(ctx: CanvasRenderingContext2D, fill: string) {
  ctx.fillStyle = col(fill);
  ctx.fill();
}

function ell(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot = 0) {
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, rot, 0, TAU);
}

function dot(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: string) {
  ctx.beginPath();
  ctx.arc(x, y, r, 0, TAU);
  fillOnly(ctx, c);
}

function stroke(ctx: CanvasRenderingContext2D, c: string, lw: number) {
  ctx.strokeStyle = col(c);
  ctx.lineWidth = lw;
  ctx.stroke();
}

// Thick outlined line, for arms, tails and stalks.
function noodle(ctx: CanvasRenderingContext2D, pts: [number, number][], w: number, fill: string, line: string) {
  ctx.beginPath();
  ctx.moveTo(pts[0][0], pts[0][1]);
  if (pts.length === 3) ctx.quadraticCurveTo(pts[1][0], pts[1][1], pts[2][0], pts[2][1]);
  else if (pts.length === 4) ctx.bezierCurveTo(pts[1][0], pts[1][1], pts[2][0], pts[2][1], pts[3][0], pts[3][1]);
  else for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
  stroke(ctx, line, w + 4);
  stroke(ctx, fill, w);
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, rgb: string, a: number) {
  if (mono) return;
  const g = ctx.createRadialGradient(x, y, 0, x, y, r);
  g.addColorStop(0, `rgba(${rgb},${a})`);
  g.addColorStop(1, `rgba(${rgb},0)`);
  ctx.fillStyle = g;
  ctx.fillRect(x - r, y - r, r * 2, r * 2);
}

function shadow(ctx: CanvasRenderingContext2D, rx: number) {
  if (mono) return;
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ell(ctx, 0, 0, rx, rx * 0.2);
  ctx.fill();
}

function highlight(ctx: CanvasRenderingContext2D, x: number, y: number, rx: number, ry: number, rot = -0.3) {
  if (mono) return;
  ctx.fillStyle = "rgba(255,255,255,0.38)";
  ell(ctx, x, y, rx, ry, rot);
  ctx.fill();
}

const blinking = (t: number, seed: number) => (t * 0.31 + seed) % 1 < 0.035;

// Two eyes (or one) that follow the shared expressions.
function eyes(ctx: CanvasRenderingContext2D, face: Face, t: number, xs: number[], y: number, r: number, color = "#2a1620", seed = 0) {
  const c = col(color);
  ctx.lineCap = "round";
  for (const x of xs) {
    if (face === "happy") {
      ctx.beginPath();
      ctx.arc(x, y + r * 0.3, r * 0.8, Math.PI * 1.1, Math.PI * 1.9);
      stroke(ctx, color, r * 0.45);
    } else if (face === "ouch") {
      const q = r * 0.7;
      const d = x < 0 ? 1 : -1;
      ctx.beginPath();
      ctx.moveTo(x - q * d, y - q);
      ctx.lineTo(x + q * d * 0.6, y);
      ctx.lineTo(x - q * d, y + q);
      stroke(ctx, color, r * 0.42);
    } else if (face === "sleepy" || blinking(t, seed)) {
      ctx.beginPath();
      ctx.arc(x, y - r * 0.2, r * 0.75, Math.PI * 0.15, Math.PI * 0.85);
      stroke(ctx, color, r * 0.38);
    } else {
      ctx.fillStyle = c;
      ell(ctx, x, y, r * 0.88, r);
      ctx.fill();
      if (!mono) {
        dot(ctx, x - r * 0.3, y - r * 0.38, r * 0.36, "#ffffff");
        dot(ctx, x + r * 0.3, y + r * 0.32, r * 0.16, "#ffffff");
      }
    }
  }
}

function mouth(ctx: CanvasRenderingContext2D, face: Face, x: number, y: number, w: number, line: string) {
  ctx.lineCap = "round";
  ctx.beginPath();
  if (face === "happy") {
    ctx.moveTo(x - w, y - w * 0.2);
    ctx.quadraticCurveTo(x, y + w * 1.3, x + w, y - w * 0.2);
    ctx.closePath();
    ink(ctx, "#c2405e", line, w * 0.3);
    return;
  }
  if (face === "ouch") {
    ctx.moveTo(x - w, y + w * 0.2);
    ctx.quadraticCurveTo(x - w * 0.5, y - w * 0.4, x, y + w * 0.2);
    ctx.quadraticCurveTo(x + w * 0.5, y + w * 0.8, x + w, y + w * 0.2);
  } else if (face === "sleepy") {
    ctx.arc(x, y + w * 0.2, w * 0.35, 0, TAU);
  } else {
    ctx.moveTo(x - w, y);
    ctx.quadraticCurveTo(x - w / 2, y + w * 0.7, x, y);
    ctx.quadraticCurveTo(x + w / 2, y + w * 0.7, x + w, y);
  }
  stroke(ctx, line, w * 0.32);
}

function blush(ctx: CanvasRenderingContext2D, xs: number[], y: number, r: number, color = "#ff7f9f") {
  if (mono) return;
  ctx.globalAlpha = 0.5;
  for (const x of xs) {
    ctx.fillStyle = color;
    ell(ctx, x, y, r, r * 0.6);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function zzz(ctx: CanvasRenderingContext2D, x: number, y: number, t: number) {
  if (mono) return;
  ctx.font = "800 14px system-ui, sans-serif";
  ctx.textAlign = "center";
  for (let k = 0; k < 2; k++) {
    const u = (t * 0.5 + k * 0.5) % 1;
    ctx.globalAlpha = 1 - u;
    ctx.fillStyle = "#cfe2ff";
    ctx.fillText("z", x + u * 14, y - u * 22);
  }
  ctx.globalAlpha = 1;
}

function star(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, points = 5) {
  ctx.beginPath();
  for (let k = 0; k < points * 2; k++) {
    const rr = k % 2 === 0 ? r : r * 0.45;
    const a = -Math.PI / 2 + (k * Math.PI) / points;
    ctx.lineTo(x + Math.cos(a) * rr, y + Math.sin(a) * rr);
  }
  ctx.closePath();
  fillOnly(ctx, color);
}

function flame(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, t: number, seed: number, rot = 0) {
  const fl = Math.sin(t * 10 + seed) * 0.12 + Math.sin(t * 17 + seed * 2) * 0.06;
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(rot);
  const layers: [string, number][] = [
    ["#ff6b3a", 1],
    ["#ffb347", 0.68],
    ["#fff1a8", 0.36],
  ];
  for (const [c, k] of layers) {
    ctx.beginPath();
    ctx.moveTo(-w * k, 0);
    ctx.quadraticCurveTo(-w * k, -h * k * 0.6, w * fl * 3, -h * k * (1 + fl));
    ctx.quadraticCurveTo(w * k, -h * k * 0.6, w * k, 0);
    ctx.quadraticCurveTo(0, h * 0.25 * k, -w * k, 0);
    fillOnly(ctx, c);
  }
  ctx.restore();
}

// ---------- axolotls ----------

type GillStyle = "feather" | "flame" | "leaf" | "glow";

function gill(ctx: CanvasRenderingContext2D, m: Morph, style: GillStyle, bx: number, by: number, ang: number, len: number, w: number, t: number, seed: number) {
  const ex = bx + Math.cos(ang) * len;
  const ey = by + Math.sin(ang) * len;
  if (style === "flame") {
    noodle(ctx, [[bx, by], [ex, ey]], w, m.gill, m.outline);
    flame(ctx, ex, ey + 3, w * 1.6, w * 4.2, t, seed, ang + Math.PI / 2);
    return;
  }
  if (style === "leaf") {
    noodle(ctx, [[bx, by], [ex, ey]], w * 0.8, "#4f9a3c", "#24502a");
    ctx.save();
    ctx.translate(ex, ey);
    ctx.rotate(ang);
    ctx.beginPath();
    ctx.moveTo(-2, 0);
    ctx.quadraticCurveTo(w * 2.4, -w * 2.2, w * 4.6, 0);
    ctx.quadraticCurveTo(w * 2.4, w * 2.2, -2, 0);
    ink(ctx, "#6cc45a", "#24502a", 1.8);
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(w * 4, 0);
    stroke(ctx, "#3f8a3a", 1.2);
    ctx.restore();
    return;
  }
  const gc = style === "glow" ? "#cfe8ff" : m.gill;
  const tip = style === "glow" ? "#ffffff" : m.gillTip;
  if (style === "glow") glow(ctx, ex, ey, w * 5, "170,215,255", 0.35);
  for (let pass = 0; pass < 2; pass++) {
    const c = pass === 0 ? m.outline : gc;
    const grow = pass === 0 ? w * 0.5 : 0;
    ctx.beginPath();
    ctx.moveTo(bx, by);
    ctx.lineTo(ex, ey);
    stroke(ctx, c, w + grow);
    for (let j = 0; j < 3; j++) {
      const u = 0.45 + j * 0.22;
      const side = j % 2 === 0 ? 1 : -1;
      const px = bx + Math.cos(ang) * len * u - Math.sin(ang) * w * side;
      const py = by + Math.sin(ang) * len * u + Math.cos(ang) * w * side;
      ell(ctx, px, py, w * 1.2 + grow / 2, w * 0.7 + grow / 2, ang + side * 0.6);
      fillOnly(ctx, c);
    }
    ctx.beginPath();
    ctx.arc(ex, ey, w * 0.9 + grow / 2, 0, TAU);
    fillOnly(ctx, pass === 0 ? m.outline : tip);
  }
}

interface AxoExtras {
  gills?: GillStyle;
  behindHead?: () => void;
  onHead?: () => void;
  onBody?: () => void;
  arms?: "stubby" | "none";
}

// Sitting, front-facing chibi axolotl shared by the starters and Boinglotl.
function axolotl(ctx: CanvasRenderingContext2D, m: Morph, t: number, face: Face, ex: AxoExtras = {}) {
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  shadow(ctx, 34);
  const sway = Math.sin(t * 2) * 3;
  ctx.beginPath();
  ctx.moveTo(14, -10);
  ctx.quadraticCurveTo(40, -6, 46 + sway, -28);
  ctx.quadraticCurveTo(36, -20, 14, -24);
  ctx.closePath();
  ink(ctx, m.body, m.outline);
  ctx.beginPath();
  ctx.moveTo(22, -22);
  ctx.quadraticCurveTo(40, -34, 47 + sway, -30);
  ctx.quadraticCurveTo(38, -24, 22, -18);
  ink(ctx, m.fin, m.outline, 1.6);

  for (const s of [-1, 1]) {
    ell(ctx, s * 13, -4, 9, 5);
    ink(ctx, m.body, m.outline, 2);
  }
  ell(ctx, 0, -28, 24, 26);
  ink(ctx, m.body, m.outline);
  ell(ctx, 0, -22, 15, 15);
  fillOnly(ctx, m.belly);
  if (!mono) {
    ctx.globalAlpha = 0.3;
    ell(ctx, -14, -28, 9, 20);
    fillOnly(ctx, m.shade);
    ctx.globalAlpha = 1;
  }
  ex.onBody?.();
  if (ex.arms !== "none") {
    for (const s of [-1, 1]) {
      ell(ctx, s * 21, -24, 6, 9, s * 0.5);
      ink(ctx, m.body, m.outline, 2);
    }
  }

  const hy = -64;
  ex.behindHead?.();
  const flap = Math.sin(t * 4) * 0.07;
  const style = ex.gills ?? "feather";
  for (let k = 0; k < 3; k++) {
    const a = [-0.8, -0.3, 0.2][k];
    gill(ctx, m, style, -28, hy - 6 + k * 7, Math.PI - a - flap, 20 - k * 2, 3.6, t, k);
    gill(ctx, m, style, 28, hy - 6 + k * 7, a + flap, 20 - k * 2, 3.6, t, k + 3);
  }
  ell(ctx, 0, hy, 34, 27);
  ink(ctx, m.body, m.outline, 3);
  if (!mono) {
    ctx.save();
    ell(ctx, 0, hy, 34, 27);
    ctx.clip();
    ctx.globalAlpha = 0.3;
    ell(ctx, -22, hy + 6, 16, 26);
    fillOnly(ctx, m.shade);
    ctx.globalAlpha = 1;
    ctx.restore();
  }
  highlight(ctx, -10, hy - 16, 12, 5);
  for (const [sx, sy] of [[18, -78], [24, -70], [-20, -36]] as const) dot(ctx, sx, sy, 2.4, m.spots);
  eyes(ctx, face, t, [-13, 13], hy, 5.6, m.eye, 0.2);
  blush(ctx, [-22, 22], hy + 9, 5, m.blush);
  mouth(ctx, face, 0, hy + 10, 5, m.outline);
  ex.onHead?.();
}

function bubbleRing(ctx: CanvasRenderingContext2D, t: number, cy: number, rx: number, ry: number, n: number, front: boolean) {
  for (let k = 0; k < n; k++) {
    const a = t * 0.9 + (k * TAU) / n;
    if (Math.sin(a) > 0 !== front) continue;
    const x = Math.cos(a) * rx;
    const y = cy + Math.sin(a) * ry;
    const r = 4 + (k % 3);
    ctx.beginPath();
    ctx.arc(x, y, r, 0, TAU);
    if (mono) fillOnly(ctx, "#000");
    else {
      ctx.fillStyle = "rgba(170,225,255,0.45)";
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.85)";
      ctx.lineWidth = 1.3;
      ctx.stroke();
      dot(ctx, x - r * 0.35, y - r * 0.35, r * 0.25, "#ffffff");
    }
  }
}

function drawSplashlotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  bubbleRing(ctx, t, -70, 48, 14, 6, false);
  axolotl(ctx, m, t, face);
  bubbleRing(ctx, t, -70, 48, 14, 6, true);
}

function drawTidelotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  ctx.scale(1.14, 1.14);
  bubbleRing(ctx, t, -66, 56, 18, 8, false);
  axolotl(ctx, m, t, face, {
    behindHead: () => {
      // A mane of curling waves rising from the back of the head.
      const waves: [number, number, number][] = [
        [-22, 0.8, 0],
        [-2, 1.1, 1.3],
        [18, 0.85, 2.6],
      ];
      for (const [wx, k, ph] of waves) {
        const bob = Math.sin(t * 2.2 + ph) * 2;
        const top = -84 - 30 * k + bob;
        ctx.beginPath();
        ctx.moveTo(wx - 12, -80);
        ctx.quadraticCurveTo(wx - 14, top + 6, wx + 2, top);
        ctx.quadraticCurveTo(wx + 16, top - 2, wx + 15, top + 12);
        ctx.quadraticCurveTo(wx + 8, top + 4, wx + 4, top + 12);
        ctx.quadraticCurveTo(wx + 4, -86, wx + 12, -78);
        ctx.closePath();
        ink(ctx, "#4aa8ff", "#1f5a9a", 2.4);
        ctx.beginPath();
        ctx.moveTo(wx - 6, top + 4);
        ctx.quadraticCurveTo(wx + 8, top - 4, wx + 14, top + 8);
        stroke(ctx, "#ffffff", 3);
        dot(ctx, wx + 14, top + 13, 2.2, "#ffffff");
      }
    },
    onHead: () => {
      for (let k = 0; k < 3; k++) {
        const u = (t * 0.5 + k / 3) % 1;
        dot(ctx, -30 + k * 30, -98 - u * 26, 2.2 * (1 - u) + 0.6, "#bfe6ff");
      }
    },
  });
  bubbleRing(ctx, t, -66, 56, 18, 8, true);
}

function drawEmberlotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  axolotl(ctx, m, t, face, { gills: "flame" });
}

function drawBlazelotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  ctx.scale(1.14, 1.14);
  axolotl(ctx, m, t, face, {
    gills: "flame",
    behindHead: () => {
      glow(ctx, 0, -70, 70, "255,150,60", 0.35);
      for (let k = 0; k < 7; k++) {
        const a = -Math.PI / 2 + (k - 3) * 0.42;
        flame(ctx, Math.cos(a) * 26, -66 + Math.sin(a) * 22, 10, 30 + (k % 2) * 8, t, k * 1.7, a + Math.PI / 2);
      }
    },
    onBody: () => {
      flame(ctx, 47, -30, 6, 16, t, 9, 0.5);
    },
  });
}

function drawSproutlotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  axolotl(ctx, m, t, face, {
    gills: "leaf",
    onHead: () => {
      const sw = Math.sin(t * 2) * 0.12;
      noodle(ctx, [[0, -90], [2, -100]], 2.5, "#4f9a3c", "#24502a");
      for (const s of [-1, 1]) {
        ctx.save();
        ctx.translate(2, -100);
        ctx.rotate(s * (0.7 + sw));
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(s * 6, -10, s * 2, -18);
        ctx.quadraticCurveTo(-s * 6, -8, 0, 0);
        ink(ctx, "#7fd06a", "#24502a", 1.8);
        ctx.restore();
      }
    },
  });
}

function drawGrovelotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  ctx.scale(1.14, 1.14);
  axolotl(ctx, m, t, face, {
    gills: "leaf",
    onBody: () => {
      ctx.beginPath();
      ctx.moveTo(-28, -46);
      ctx.quadraticCurveTo(0, -38, 28, -46);
      ctx.quadraticCurveTo(34, -24, 26, -6);
      ctx.quadraticCurveTo(14, -12, 0, -8);
      ctx.quadraticCurveTo(-14, -12, -26, -6);
      ctx.quadraticCurveTo(-34, -24, -28, -46);
      ink(ctx, "#4f8f3a", "#24502a", 2.4);
      for (let k = 0; k < 7; k++) dot(ctx, -22 + k * 7.5, -12 - (k % 2) * 4, 2.6, "#6cb04a");
      for (const [fx, fy, c] of [[-16, -30, "#ffd166"], [12, -22, "#ff9ec4"], [20, -38, "#ffffff"]] as const) {
        for (let a = 0; a < 5; a++) dot(ctx, fx + Math.cos((a * TAU) / 5) * 2.6, fy + Math.sin((a * TAU) / 5) * 2.6, 2, c);
        dot(ctx, fx, fy, 1.5, "#f2a33a");
      }
    },
    onHead: () => {
      for (let k = 0; k < 5; k++) {
        const a = -Math.PI / 2 + (k - 2) * 0.4;
        ctx.save();
        ctx.translate(Math.cos(a) * 28, -64 + Math.sin(a) * 24);
        ctx.rotate(a + Math.PI / 2);
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.quadraticCurveTo(7, -9, 0, -20);
        ctx.quadraticCurveTo(-7, -9, 0, 0);
        ink(ctx, k % 2 ? "#7fd06a" : "#5cb85a", "#24502a", 1.8);
        ctx.restore();
      }
    },
  });
}

function drawBoinglotl(ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) {
  const boing = Math.sin(t * 5);
  ctx.scale(1 - boing * 0.03, 1 + boing * 0.04);
  axolotl(ctx, m, t, face, {
    arms: "none",
    onBody: () => {
      for (const s of [-1, 1]) {
        const wx = Math.sin(t * 4 + s) * 8;
        const wy = Math.cos(t * 3 + s) * 10;
        const hx = s * (56 + wx);
        const hy = -46 + wy;
        noodle(ctx, [[s * 18, -32], [s * 34, -10 + wy], [s * 44, -60 - wy], [hx, hy]], 6, m.body, m.outline);
        ell(ctx, hx, hy, 7, 6);
        ink(ctx, m.body, m.outline, 2);
      }
    },
    onHead: () => {
      ell(ctx, 0, -88, 42, 9);
      ink(ctx, "#f2d27a", "#9c7a2c", 2);
      ctx.beginPath();
      ctx.ellipse(0, -90, 23, 17, 0, Math.PI, TAU);
      ctx.closePath();
      ink(ctx, "#f2d27a", "#9c7a2c", 2);
      ctx.beginPath();
      ctx.rect(-22.5, -97, 45, 6);
      fillOnly(ctx, "#e0524f");
    },
  });
}

// ---------- meadow ----------

function drawBumblebun(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const body = "#ffd84a";
  const line = "#9a6a10";
  shadow(ctx, 30);
  const flap = Math.abs(Math.sin(t * 26));
  for (const s of [-1, 1]) {
    ctx.save();
    ctx.translate(s * 22, -54);
    ctx.scale(s * (0.6 + flap * 0.4), 1);
    ell(ctx, 16, -10, 18, 11, -0.5);
    if (mono) fillOnly(ctx, "#000");
    else {
      ctx.fillStyle = "rgba(230,245,255,0.6)";
      ctx.fill();
      ctx.strokeStyle = "rgba(120,160,200,0.8)";
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }
    ctx.restore();
  }
  const ear = Math.sin(t * 2) * 0.06;
  for (const s of [-1, 1]) {
    ell(ctx, s * 12, -82, 8, 22, s * (0.22 + ear));
    ink(ctx, body, line);
    ell(ctx, s * 12.5, -80, 4, 15, s * (0.22 + ear));
    fillOnly(ctx, "#ffb3c8");
  }
  ell(ctx, 26, -14, 9, 8);
  ink(ctx, "#ffffff", "#c8c8c8", 2);
  ell(ctx, 0, -34, 31, 31);
  ink(ctx, body, line, 3);
  ctx.save();
  ell(ctx, 0, -34, 31, 31);
  ctx.clip();
  for (const y of [-24, -13]) {
    ctx.beginPath();
    ctx.rect(-40, y, 80, 7);
    fillOnly(ctx, "#3a2a1a");
  }
  ctx.restore();
  for (const s of [-1, 1]) {
    ell(ctx, s * 13, -3, 9, 5);
    ink(ctx, body, line, 2);
  }
  highlight(ctx, -12, -54, 10, 5);
  eyes(ctx, face, t, [-11, 11], -40, 5, "#2a1620", 0.5);
  blush(ctx, [-19, 19], -31, 4.5);
  mouth(ctx, face, 0, -30, 4, line);
  if (face === "normal" || face === "happy") {
    const u = (t * 0.4) % 1;
    ctx.globalAlpha = mono ? 1 : 1 - u;
    ctx.font = "800 18px system-ui, sans-serif";
    ctx.textAlign = "center";
    ctx.fillStyle = col("#7a5cd6");
    ctx.fillText("♪", 34 + u * 10, -66 - u * 24);
    ctx.globalAlpha = 1;
  }
}

function drawGlimmoth(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  shadow(ctx, 20);
  const hover = Math.sin(t * 2) * 5;
  ctx.translate(0, -12 + hover);
  glow(ctx, 0, -56, 70, "255,235,150", 0.35);
  const flap = 0.85 + Math.sin(t * 6) * 0.15;
  for (const s of [-1, 1]) {
    ctx.save();
    ctx.scale(s, 1);
    ctx.save();
    ctx.translate(10, -58);
    ctx.scale(flap, 1);
    ell(ctx, 24, -10, 30, 21, -0.35);
    ink(ctx, "#d8c4ff", "#7a5cb0", 2.4);
    ell(ctx, 18, 18, 20, 14, 0.4);
    ink(ctx, "#f2d6ff", "#7a5cb0", 2.4);
    for (const [sx, sy, r] of [[28, -14, 4.5], [40, -4, 3], [16, -20, 3], [20, 18, 3.5]] as const) {
      const tw = 0.6 + 0.4 * Math.sin(t * 4 + sx + sy);
      star(ctx, sx, sy, r * tw + 1, "#ffe066", 4);
    }
    ctx.restore();
    ctx.restore();
  }
  ell(ctx, 0, -40, 15, 22);
  ink(ctx, "#fff3d6", "#b8a06a");
  for (let k = 0; k < 7; k++) {
    const a = Math.PI + (k / 6) * Math.PI;
    dot(ctx, Math.cos(a) * 14, -56 - Math.sin(a) * -4, 6, "#ffffff");
  }
  ell(ctx, 0, -66, 16, 15);
  ink(ctx, "#fff8e8", "#b8a06a");
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * 6, -78);
    ctx.quadraticCurveTo(s * 10, -94, s * 18, -100);
    stroke(ctx, "#8a6a3a", 2);
    for (let k = 1; k < 5; k++) {
      const u = k / 5;
      const bx = s * (6 + 12 * u);
      const by = -78 - 22 * u;
      ctx.beginPath();
      ctx.moveTo(bx, by);
      ctx.lineTo(bx + s * 5, by + 2);
      stroke(ctx, "#8a6a3a", 1.4);
    }
  }
  eyes(ctx, face, t, [-6, 6], -66, 4.6, "#3a2a5a", 0.1);
  blush(ctx, [-11, 11], -59, 3.4);
  mouth(ctx, face, 0, -58, 3, "#8a6a3a");
}

function drawSnailor(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const skin = "#a8dcea";
  const line = "#3a7a92";
  shadow(ctx, 40);
  ell(ctx, 2, -8, 40, 9);
  ink(ctx, skin, line);
  ell(ctx, 16, -40, 27, 27);
  ink(ctx, "#4fa6c4", "#1f5a74", 3);
  ctx.beginPath();
  for (let k = 0; k < 40; k++) {
    const a = k * 0.32;
    const r = 22 - k * 0.5;
    ctx.lineTo(16 + Math.cos(a) * r, -40 + Math.sin(a) * r);
  }
  stroke(ctx, "#1f5a74", 2.4);
  highlight(ctx, 6, -58, 9, 4);
  const sway = Math.sin(t * 1.8) * 2;
  ctx.beginPath();
  ctx.moveTo(-34, -6);
  ctx.quadraticCurveTo(-38, -40, -24 + sway, -56);
  ctx.quadraticCurveTo(-8 + sway, -58, -10, -8);
  ctx.closePath();
  ink(ctx, skin, line);
  for (const [sx, ex] of [[-28, -34], [-16, -10]] as const) {
    noodle(ctx, [[sx + sway, -56], [ex + sway, -82]], 3, skin, line);
    ell(ctx, ex + sway, -84, 6.5, 6.5);
    ink(ctx, "#ffffff", line, 2);
  }
  eyes(ctx, face, t, [-34 + sway, -10 + sway], -84, 3.6, "#1a2a3a", 0.7);
  ctx.beginPath();
  ctx.ellipse(-21 + sway, -52, 14, 10, 0, Math.PI, TAU);
  ctx.closePath();
  ink(ctx, "#cfd8e3", "#5a6878", 2);
  ctx.beginPath();
  ctx.rect(-35 + sway, -54, 28, 4);
  fillOnly(ctx, "#9aa8b8");
  ctx.beginPath();
  ctx.moveTo(-21 + sway, -62);
  ctx.quadraticCurveTo(-14 + sway, -74, -4 + sway + Math.sin(t * 3) * 2, -72);
  stroke(ctx, "#e0524f", 4);
  blush(ctx, [-30 + sway, -14 + sway], -40, 3.2);
  mouth(ctx, face, -22 + sway, -40, 3.5, line);
  ctx.save();
  ctx.translate(-2, -16);
  ctx.scale(0.85, 0.85);
  ctx.beginPath();
  ctx.moveTo(-11, -12);
  ctx.quadraticCurveTo(0, -16, 11, -12);
  ctx.quadraticCurveTo(12, 6, 0, 14);
  ctx.quadraticCurveTo(-12, 6, -11, -12);
  ink(ctx, "#cfd8e3", "#5a6878", 2.4);
  for (let k = -2; k <= 2; k++) {
    ctx.beginPath();
    ctx.moveTo(0, 10);
    ctx.lineTo(k * 4.5, -11);
    stroke(ctx, "#8fa2b8", 1.2);
  }
  ctx.restore();
}

// ---------- pond ----------

function drawFroggle(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const skin = "#5ec48a";
  const line = "#22704a";
  shadow(ctx, 36);
  for (const s of [-1, 1]) {
    ell(ctx, s * 26, -12, 14, 11, s * 0.3);
    ink(ctx, skin, line);
    ell(ctx, s * 34, -2, 11, 4);
    ink(ctx, skin, line, 2);
  }
  ell(ctx, 0, -28, 30, 24);
  ink(ctx, skin, line, 3);
  ell(ctx, 0, -20, 20, 14);
  fillOnly(ctx, "#c8f0c8");
  for (const s of [-1, 1]) {
    ell(ctx, s * 10, -4, 7, 4);
    ink(ctx, skin, line, 2);
  }
  const tail = Math.sin(t * 6);
  for (const k of [0, 1]) {
    ctx.beginPath();
    ctx.moveTo(26, -42);
    ctx.quadraticCurveTo(40, -46 + tail * 4 + k * 6, 50 + k * 2, -38 + tail * 6 + k * 8);
    stroke(ctx, "#e0524f", 4);
  }
  ctx.beginPath();
  ctx.moveTo(-29, -38);
  ctx.quadraticCurveTo(0, -46, 29, -38);
  stroke(ctx, "#e0524f", 7);
  for (const s of [-1, 1]) {
    ell(ctx, s * 14, -52, 11, 11);
    ink(ctx, skin, line, 2.6);
    ell(ctx, s * 14, -52, 7.5, 7.5);
    fillOnly(ctx, "#ffffff");
  }
  eyes(ctx, face, t, [-14, 14], -52, 4.6, "#1a2a1a", 0.3);
  blush(ctx, [-20, 20], -30, 4.5);
  ctx.beginPath();
  if (face === "happy") {
    ctx.moveTo(-14, -30);
    ctx.quadraticCurveTo(0, -18, 14, -30);
    ctx.closePath();
    ink(ctx, "#c2405e", line, 2);
  } else if (face === "ouch") mouth(ctx, face, 0, -28, 7, line);
  else {
    ctx.moveTo(-14, -30);
    ctx.quadraticCurveTo(0, -24, 14, -30);
    stroke(ctx, line, 2.4);
  }
}

function drawLilypadda(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const skin = "#9ccf6a";
  const line = "#4a7a2a";
  shadow(ctx, 44);
  for (const [lx, ly] of [[-26, -6], [22, -6], [-10, -2], [8, -2]] as const) {
    ell(ctx, lx, ly, 8, 6);
    ink(ctx, skin, line, 2);
  }
  ell(ctx, 0, -18, 36, 18);
  ink(ctx, "#5f9a4a", "#2f5a28", 2.6);
  ctx.beginPath();
  ctx.ellipse(0, -30, 44, 18, 0, 0.18, TAU - 0.18);
  ctx.lineTo(0, -30);
  ctx.closePath();
  ink(ctx, "#4fb05a", "#24602b", 2.6);
  for (let k = 0; k < 7; k++) {
    const a = 0.5 + (k * (TAU - 1)) / 6;
    ctx.beginPath();
    ctx.moveTo(0, -30);
    ctx.lineTo(Math.cos(a) * 38, -30 + Math.sin(a) * 15);
    stroke(ctx, "#3f9a48", 1.4);
  }
  const bob = Math.sin(t * 2) * 1.5;
  for (let k = 0; k < 6; k++) {
    const a = (k / 6) * TAU + t * 0.2;
    ell(ctx, 8 + Math.cos(a) * 7, -40 + bob + Math.sin(a) * 3.5, 6, 3.5, a);
    ink(ctx, "#ffb3cf", "#c0507a", 1.4);
  }
  dot(ctx, 8, -42 + bob, 3.5, "#ffd166");
  ctx.beginPath();
  ctx.moveTo(-34, -18);
  ctx.quadraticCurveTo(-48, -22, -46, -34);
  stroke(ctx, line, 12);
  stroke(ctx, skin, 8);
  ell(ctx, -48, -40, 14, 12);
  ink(ctx, skin, line);
  highlight(ctx, -52, -48, 5, 3);
  eyes(ctx, face, t, [-54, -42], -42, 3.6, "#1a2a1a", 0.6);
  blush(ctx, [-58, -38], -36, 3);
  mouth(ctx, face, -48, -35, 3, line);
}

function drawNewtling(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const skin = "#ff7a3a";
  const line = "#9a3a14";
  shadow(ctx, 40);
  const sw = Math.sin(t * 2.4) * 4;
  ctx.beginPath();
  ctx.moveTo(26, -14);
  ctx.quadraticCurveTo(52, -14, 58 + sw, -34);
  ctx.quadraticCurveTo(48, -22, 26, -24);
  ctx.closePath();
  ink(ctx, skin, line);
  for (const [lx, ly] of [[-18, -4], [16, -4], [-6, -2], [26, -6]] as const) {
    ell(ctx, lx, ly, 6, 5);
    ink(ctx, skin, line, 2);
  }
  ell(ctx, 2, -18, 30, 12);
  ink(ctx, skin, line);
  for (const [sx, sy] of [[-6, -24], [8, -22], [20, -18], [-14, -18], [36, -22]] as const) dot(ctx, sx, sy, 2.6, "#ffcf5a");
  ell(ctx, -26, -28, 17, 14);
  ink(ctx, skin, line);
  highlight(ctx, -30, -36, 6, 3);
  eyes(ctx, face, t, [-34, -20], -29, 4, "#2a1206", 0.8);
  blush(ctx, [-38, -14], -22, 3.4);
  mouth(ctx, face, -27, -21, 3.4, line);
  ctx.beginPath();
  ctx.moveTo(-40, -38);
  ctx.lineTo(-22, -76 + Math.sin(t * 2) * 2);
  ctx.lineTo(-12, -38);
  ctx.closePath();
  ink(ctx, "#5b3fa6", "#2c1c58", 2.4);
  star(ctx, -24, -54, 4, "#ffd166");
  ell(ctx, -26, -38, 20, 5);
  ink(ctx, "#5b3fa6", "#2c1c58", 2);
}

// ---------- hollow ----------

function drawCindermouse(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const fur = "#9a8478";
  const line = "#4a3a34";
  shadow(ctx, 30);
  const sw = Math.sin(t * 3) * 4;
  ctx.beginPath();
  ctx.moveTo(14, -10);
  ctx.quadraticCurveTo(46, -10, 46 + sw, -44);
  stroke(ctx, line, 8);
  stroke(ctx, "#c8a49a", 4.5);
  glow(ctx, 46 + sw, -48, 26, "255,150,60", 0.55);
  flame(ctx, 46 + sw, -42, 7, 18, t, 2);
  for (let k = 0; k < 3; k++) {
    const u = (t * 0.9 + k / 3) % 1;
    dot(ctx, 46 + sw + Math.sin(k * 4 + t * 3) * 6, -54 - u * 22, 1.6, "#ffb347");
  }
  ell(ctx, 0, -24, 22, 22);
  ink(ctx, fur, line);
  ell(ctx, 0, -18, 13, 13);
  fillOnly(ctx, "#f1e2d2");
  for (const s of [-1, 1]) {
    ell(ctx, s * 9, -3, 7, 4);
    ink(ctx, "#e8b8b0", line, 2);
    ell(ctx, s * 19, -66, 14, 14);
    ink(ctx, fur, line);
    ell(ctx, s * 19, -66, 8.5, 8.5);
    fillOnly(ctx, "#ffb3c8");
  }
  ell(ctx, 0, -50, 21, 18);
  ink(ctx, fur, line);
  ctx.beginPath();
  ctx.ellipse(0, -53, 19, 6.5, 0, 0, TAU);
  fillOnly(ctx, "#3a2a3a");
  eyes(ctx, face, t, [-8, 8], -53, 4, "#ffffff", 0.4);
  dot(ctx, 0, -44, 3, "#ff8fa8");
  for (const s of [-1, 1]) {
    for (const dy of [-1, 2]) {
      ctx.beginPath();
      ctx.moveTo(s * 6, -43 + dy);
      ctx.lineTo(s * 20, -45 + dy * 2);
      stroke(ctx, "#5a4a44", 1);
    }
  }
  mouth(ctx, face, 0, -40, 3, line);
  blush(ctx, [-14, 14], -42, 3);
}

function drawBatsy(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const fur = "#6a4fb3";
  const line = "#2e1f5a";
  shadow(ctx, 22);
  ctx.beginPath();
  ctx.moveTo(-34, -100);
  ctx.quadraticCurveTo(0, -94, 36, -102);
  stroke(ctx, "#4a3020", 8);
  stroke(ctx, "#8a5a32", 5);
  ell(ctx, 30, -106, 7, 4, -0.4);
  fillOnly(ctx, "#5cb85a");
  ctx.save();
  ctx.translate(0, -98);
  ctx.rotate(Math.sin(t * 1.6) * 0.08);
  for (const s of [-1, 1]) {
    noodle(ctx, [[s * 6, 4], [s * 5, -2]], 2.5, "#3a2a2a", "#1a1010");
  }
  ell(ctx, 0, 38, 27, 30);
  ink(ctx, fur, line, 3);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * 22, 14);
    ctx.quadraticCurveTo(s * 40, 34, s * 26, 62);
    ctx.quadraticCurveTo(s * 18, 52, s * 10, 60);
    ctx.quadraticCurveTo(s * 18, 36, s * 22, 14);
    ink(ctx, "#4e3890", line, 2.4);
    ctx.beginPath();
    ctx.moveTo(s * 6, 62);
    ctx.lineTo(s * 16, 78);
    ctx.lineTo(s * 18, 60);
    ctx.closePath();
    ink(ctx, fur, line, 2);
  }
  highlight(ctx, -10, 22, 8, 4);
  for (const s of [-1, 1]) {
    ell(ctx, s * 9, 44, 7, 7.5);
    fillOnly(ctx, "#fff6d0");
  }
  eyes(ctx, face, t, [-9, 9], 44, 4.4, "#1a1030", 0.9);
  blush(ctx, [-17, 17], 50, 3.6);
  ctx.save();
  ctx.translate(0, 32);
  ctx.scale(1, -1);
  mouth(ctx, face, 0, 0, 4, line);
  ctx.restore();
  if (!mono && face !== "ouch") {
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.moveTo(s * 3, 30);
      ctx.lineTo(s * 1.5, 25);
      ctx.lineTo(s * 0.2, 30);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
    }
  }
  ctx.restore();
}

function drawMushgloom(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  shadow(ctx, 34);
  const pulse = 0.5 + 0.5 * Math.sin(t * 1.6);
  glow(ctx, 0, -50, 80, "120,230,255", 0.18 + pulse * 0.12);
  for (const s of [-1, 1]) {
    ell(ctx, s * 10, -3, 8, 4.5);
    ink(ctx, "#d9c8b4", "#7a6a58", 2);
  }
  ctx.beginPath();
  ctx.moveTo(-17, -4);
  ctx.quadraticCurveTo(-20, -30, -14, -44);
  ctx.lineTo(14, -44);
  ctx.quadraticCurveTo(20, -30, 17, -4);
  ctx.quadraticCurveTo(0, 2, -17, -4);
  ink(ctx, "#efe2d0", "#8a7a64");
  const sway = Math.sin(t * 1.2) * 0.04;
  ctx.save();
  ctx.translate(0, -44);
  ctx.rotate(sway);
  ctx.beginPath();
  ctx.ellipse(0, 0, 46, 34, 0, Math.PI, TAU);
  ctx.quadraticCurveTo(0, 12, -46, 0);
  ink(ctx, "#7a4fb0", "#3a2060", 3);
  for (const [sx, sy, r] of [[-22, -14, 6], [4, -24, 7], [24, -10, 5], [-6, -6, 4], [32, -2, 3.5], [-36, -2, 3.5]] as const) {
    const g = 0.6 + 0.4 * Math.sin(t * 2 + sx);
    glow(ctx, sx, sy, r * 3, "140,240,255", 0.4 * g);
    dot(ctx, sx, sy, r, mono ? "#000" : `rgba(150,245,255,${0.7 + 0.3 * g})`);
  }
  highlight(ctx, -18, -24, 10, 4);
  ctx.restore();
  const f: Face = face === "normal" ? "sleepy" : face;
  eyes(ctx, f, t, [-7, 7], -26, 4, "#3a2a4a");
  blush(ctx, [-11, 11], -19, 3.2);
  mouth(ctx, face, 0, -17, 3, "#8a7a64");
  if (face === "normal" || face === "sleepy") zzz(ctx, 22, -60, t);
}

function drawBeetlight(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const shell = "#2f3a6b";
  const line = "#141a36";
  shadow(ctx, 34);
  const pulse = 0.5 + 0.5 * Math.sin(t * 2.2);
  glow(ctx, 0, -18, 60, "255,225,100", 0.35 + pulse * 0.2);
  for (const s of [-1, 1]) {
    for (let k = 0; k < 3; k++) {
      ctx.beginPath();
      ctx.moveTo(s * 18, -22 + k * 7);
      ctx.lineTo(s * (32 + k * 2), -10 + k * 4);
      ctx.lineTo(s * (34 + k * 2), -2);
      stroke(ctx, line, 3.2);
    }
  }
  ell(ctx, 0, -18, 22, 15);
  ink(ctx, mono ? "#000" : `rgb(255,${220 + pulse * 30},${90 + pulse * 60})`, "#a88a20", 2.4);
  ell(ctx, 0, -44, 30, 26);
  ink(ctx, shell, line, 3);
  ctx.beginPath();
  ctx.moveTo(0, -70);
  ctx.lineTo(0, -20);
  stroke(ctx, line, 2);
  highlight(ctx, -12, -56, 9, 5);
  highlight(ctx, 12, -56, 7, 4);
  ctx.beginPath();
  ctx.moveTo(-4, -76);
  ctx.quadraticCurveTo(-2, -96, 10, -100);
  ctx.quadraticCurveTo(4, -90, 4, -76);
  ink(ctx, "#4a5a9a", line, 2);
  ell(ctx, 0, -68, 17, 12);
  ink(ctx, "#3a4680", line, 2.4);
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * 5, -78);
    ctx.quadraticCurveTo(s * 14, -88, s * 20, -86);
    stroke(ctx, line, 1.6);
  }
  ell(ctx, -7, -68, 6, 6);
  fillOnly(ctx, "#ffffff");
  ell(ctx, 7, -68, 6, 6);
  fillOnly(ctx, "#ffffff");
  eyes(ctx, face, t, [-7, 7], -68, 3.6, "#141a36", 0.15);
  mouth(ctx, face, 0, -60, 3, "#ffffff");
}

// ---------- cove ----------

function drawDrumbolt(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const body = "#ffd23f";
  const line = "#a8740c";
  shadow(ctx, 32);
  ctx.beginPath();
  ctx.ellipse(0, -60, 50, 42, 0, 0, TAU);
  stroke(ctx, "#b07818", 4);
  for (let k = 0; k < 5; k++) {
    const a = Math.PI + (k / 4) * Math.PI;
    const dx = Math.cos(a) * 50;
    const dy = -60 + Math.sin(a) * 42;
    ell(ctx, dx, dy + 6, 9, 4);
    ink(ctx, "#e0524f", "#6a1e14", 2);
    ctx.beginPath();
    ctx.rect(dx - 9, dy, 18, 6);
    ink(ctx, "#ffcf5a", "#6a1e14", 2);
    ell(ctx, dx, dy, 9, 4);
    ink(ctx, "#f4ead6", "#6a1e14", 2);
  }
  for (const s of [-1, 1]) {
    noodle(ctx, [[s * 8, -12], [s * 9, -2]], 3, "#ff9a3a", "#8a4a10");
    ctx.beginPath();
    ctx.moveTo(s * 9, -2);
    ctx.lineTo(s * 16, 0);
    stroke(ctx, "#ff9a3a", 3);
  }
  ell(ctx, 0, -36, 26, 28);
  ink(ctx, body, line, 3);
  ell(ctx, 0, -30, 15, 17);
  fillOnly(ctx, "#fff6d8");
  for (const s of [-1, 1]) {
    ctx.beginPath();
    ctx.moveTo(s * 20, -54);
    ctx.quadraticCurveTo(s * 40, -40, s * 30, -16);
    ctx.quadraticCurveTo(s * 22, -26, s * 20, -54);
    ink(ctx, "#ffb02e", line, 2.4);
    ctx.beginPath();
    ctx.moveTo(s * 28, -44);
    ctx.lineTo(s * 24, -36);
    ctx.lineTo(s * 30, -34);
    ctx.lineTo(s * 26, -24);
    stroke(ctx, "#ffffff", 2);
  }
  ell(ctx, 0, -72, 21, 19);
  ink(ctx, body, line, 3);
  const zap = Math.sin(t * 8) > 0.6 ? 2 : 0;
  ctx.beginPath();
  ctx.moveTo(-4, -90);
  ctx.lineTo(4, -106 - zap);
  ctx.lineTo(0, -104);
  ctx.lineTo(10, -118 - zap);
  ctx.lineTo(6, -100);
  ctx.lineTo(10, -102);
  ctx.lineTo(6, -88);
  ctx.closePath();
  ink(ctx, "#fff07a", line, 2);
  eyes(ctx, face, t, [-8, 8], -74, 4.4, "#2a1a08", 0.25);
  if (face === "normal" && !mono) {
    for (const s of [-1, 1]) {
      ctx.beginPath();
      ctx.ellipse(s * 8, -76.5, 5.2, 3.2, 0, Math.PI, TAU);
      fillOnly(ctx, body);
      ctx.beginPath();
      ctx.moveTo(s * 3, -76);
      ctx.lineTo(s * 13, -76);
      stroke(ctx, line, 1.6);
    }
  }
  ctx.beginPath();
  ctx.moveTo(-6, -66);
  ctx.lineTo(0, -58);
  ctx.lineTo(6, -66);
  ctx.closePath();
  ink(ctx, "#ff9a3a", "#8a4a10", 1.6);
  blush(ctx, [-14, 14], -64, 3.4);
}

function drawHookroc(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const skin = "#7fa84a";
  const line = "#3a5a1e";
  shadow(ctx, 36);
  const sw = Math.sin(t * 2) * 3;
  ctx.beginPath();
  ctx.moveTo(14, -12);
  ctx.quadraticCurveTo(44, -8, 54 + sw, -4);
  ctx.quadraticCurveTo(40, -20, 16, -26);
  ctx.closePath();
  ink(ctx, skin, line);
  for (let k = 0; k < 3; k++) {
    ctx.beginPath();
    ctx.moveTo(24 + k * 9, -16 + k);
    ctx.lineTo(28 + k * 9, -21 + k * 1.5);
    stroke(ctx, line, 2);
  }
  for (const s of [-1, 1]) {
    ell(ctx, s * 11, -4, 9, 5);
    ink(ctx, skin, line, 2);
  }
  ell(ctx, 0, -32, 22, 28);
  ink(ctx, skin, line, 3);
  ell(ctx, 0, -28, 13, 20);
  fillOnly(ctx, "#e9dcae");
  for (let k = 0; k < 4; k++) {
    ctx.beginPath();
    ctx.moveTo(-11, -42 + k * 9);
    ctx.lineTo(11, -42 + k * 9);
    stroke(ctx, "#c8b882", 1.4);
  }
  noodle(ctx, [[-18, -40], [-26, -24]], 6, skin, line);
  noodle(ctx, [[18, -40], [26, -32]], 6, skin, line);
  ctx.beginPath();
  ctx.moveTo(26, -32);
  ctx.lineTo(28, -20);
  ctx.arc(22, -20, 6, 0, Math.PI * 0.95, false);
  stroke(ctx, "#8a6a10", 6);
  stroke(ctx, "#ffcf4a", 3.5);
  ell(ctx, -4, -68, 22, 15);
  ink(ctx, skin, line, 3);
  ctx.beginPath();
  ctx.moveTo(-14, -74);
  ctx.quadraticCurveTo(-44, -74, -46, -64);
  ctx.quadraticCurveTo(-44, -56, -14, -58);
  ctx.closePath();
  ink(ctx, skin, line, 2.6);
  for (let k = 0; k < 4; k++) {
    ctx.beginPath();
    ctx.moveTo(-40 + k * 7, -63);
    ctx.lineTo(-37 + k * 7, -59);
    ctx.lineTo(-34 + k * 7, -63);
    fillOnly(ctx, "#ffffff");
  }
  dot(ctx, -42, -70, 1.6, line);
  for (const ex of [-6, 8]) {
    ell(ctx, ex, -80, 7, 7);
    ink(ctx, skin, line, 2);
  }
  eyes(ctx, face, t, [-6, 8], -80, 3.8, "#2a1a08", 0.6);
  blush(ctx, [-18, 10], -62, 3.4);
  const flap = Math.sin(t * 5) * 4;
  ctx.beginPath();
  ctx.moveTo(-20, -52);
  ctx.quadraticCurveTo(0, -44, 20, -52);
  ctx.lineTo(20, -46);
  ctx.quadraticCurveTo(0, -38, -20, -46);
  ctx.closePath();
  ink(ctx, "#d9a35c", "#8a5a24", 2);
  ctx.beginPath();
  ctx.moveTo(14, -48);
  ctx.quadraticCurveTo(28, -44 + flap, 36, -34 + flap);
  ctx.lineTo(30, -32 + flap);
  ctx.quadraticCurveTo(22, -40, 12, -44);
  ctx.closePath();
  ink(ctx, "#d9a35c", "#8a5a24", 2);
}

function drawSnoozicle(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const fur = "#bfe3f5";
  const line = "#4f84a8";
  shadow(ctx, 30);
  for (const s of [-1, 1]) {
    ell(ctx, s * 10, -4, 9, 5);
    ink(ctx, fur, line, 2);
  }
  ell(ctx, 0, -46, 24, 42);
  ink(ctx, fur, line, 3);
  ell(ctx, 0, -38, 14, 26);
  fillOnly(ctx, "#e6f6ff");
  const swing = Math.sin(t * 1.3) * 3;
  for (const s of [-1, 1]) {
    noodle(ctx, [[s * 18, -66], [s * 34, -44], [s * 30 + swing * s, -8]], 8, fur, line);
    for (let k = -1; k <= 1; k++) {
      ctx.beginPath();
      ctx.moveTo(s * 30 + swing * s + k * 3, -6);
      ctx.lineTo(s * 30 + swing * s + k * 4, 2);
      stroke(ctx, "#7fb0d0", 2.4);
    }
    ctx.beginPath();
    ctx.moveTo(s * 34, -40);
    ctx.lineTo(s * 36, -30);
    ctx.lineTo(s * 38, -40);
    fillOnly(ctx, "#ffffff");
  }
  ell(ctx, 0, -84, 22, 19);
  ink(ctx, fur, line, 3);
  ell(ctx, 0, -80, 15, 11);
  fillOnly(ctx, "#e6f6ff");
  for (const s of [-1, 1]) {
    ell(ctx, s * 7, -82, 6, 4.4, s * -0.4);
    fillOnly(ctx, "#7fb0d0");
  }
  ctx.beginPath();
  ctx.rect(-22, -100, 44, 7);
  ink(ctx, "#2c3a6a", "#141a36", 1.6);
  for (const s of [-1, 1]) {
    ell(ctx, s * 8, -96.5, 6, 3.5);
    fillOnly(ctx, "#4a5a9a");
  }
  const f: Face = face === "normal" ? "sleepy" : face;
  eyes(ctx, f, t, [-7, 7], -82, 3.6, "#1a2a4a");
  mouth(ctx, face === "sleepy" ? "normal" : face, 0, -74, 3, line);
  blush(ctx, [-13, 13], -76, 2.8);
  if (!mono) {
    for (let k = 0; k < 4; k++) {
      const tw = 0.5 + 0.5 * Math.sin(t * 3 + k * 2);
      star(ctx, -30 + k * 20, -112 + (k % 2) * 10, 2.5 + tw * 2, "#e6f6ff", 4);
    }
  }
  if (face === "normal" || face === "sleepy") zzz(ctx, 24, -100, t);
}

function drawReindoc(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const fur = "#a8784a";
  const line = "#5a3a1e";
  ctx.scale(0.86, 0.86);
  shadow(ctx, 26);
  for (const s of [-1, 1]) {
    noodle(ctx, [[s * 8, -16], [s * 9, -4]], 5, fur, line);
    ell(ctx, s * 9, -3, 5, 3);
    fillOnly(ctx, "#3a2414");
  }
  ell(ctx, 0, -26, 18, 18);
  ink(ctx, fur, line, 2.6);
  ell(ctx, 0, -22, 11, 11);
  fillOnly(ctx, "#f1e2d2");
  for (const s of [-1, 1]) {
    noodle(ctx, [[s * 14, -32], [s * 22, -20]], 4.5, fur, line);
  }
  ell(ctx, 0, -54, 19, 17);
  ink(ctx, fur, line, 2.6);
  for (const s of [-1, 1]) {
    ell(ctx, s * 19, -58, 8, 4.5, s * 0.5);
    ink(ctx, fur, line, 2);
  }
  for (const s of [-1, 1]) {
    noodle(ctx, [[s * 16, -72], [s * 30, -90]], 3, "#8a5a32", "#3a2414");
    noodle(ctx, [[s * 24, -82], [s * 34, -80]], 2.5, "#8a5a32", "#3a2414");
  }
  const tilt = Math.sin(t * 1.5) * 0.04;
  ctx.save();
  ctx.translate(0, -68);
  ctx.rotate(tilt);
  ell(ctx, 0, 0, 34, 8);
  ink(ctx, "#ff8fbf", "#a8406a", 2.4);
  ctx.beginPath();
  ctx.moveTo(-22, -2);
  ctx.quadraticCurveTo(-24, -34, 0, -36);
  ctx.quadraticCurveTo(24, -34, 22, -2);
  ctx.closePath();
  ink(ctx, "#ff8fbf", "#a8406a", 2.4);
  ctx.beginPath();
  ctx.rect(-22, -8, 44, 6);
  fillOnly(ctx, "#e8689f");
  ctx.beginPath();
  ctx.rect(-2.5, -28, 5, 14);
  ctx.rect(-7, -23.5, 14, 5);
  fillOnly(ctx, "#ffffff");
  highlight(ctx, -10, -26, 6, 3);
  ctx.restore();
  eyes(ctx, face, t, [-7, 7], -54, 4, "#2a1606", 0.45);
  dot(ctx, 0, -46, 4.5, "#5aa7ff");
  if (!mono) dot(ctx, -1.5, -47.5, 1.4, "#ffffff");
  mouth(ctx, face, 0, -40, 3, line);
  blush(ctx, [-12, 12], -46, 3.2);
}

// ---------- guardian ----------

function drawOlm(ctx: CanvasRenderingContext2D, t: number, face: Face) {
  const skin = "#f6e9ee";
  const belly = "#e6d2da";
  const line = "#a98a98";
  shadow(ctx, 110);
  glow(ctx, -40, -40, 150, "190,220,255", 0.22);
  // A long, wavy body drawn as a thick outlined stroke along its spine.
  const spine: [number, number][] = [];
  for (let k = 0; k <= 24; k++) {
    const u = k / 24;
    spine.push([-56 + u * 176, -28 + Math.sin(u * 5 + t * 1.1) * 6 * u + u * 6]);
  }
  const trace = () => {
    ctx.beginPath();
    ctx.moveTo(spine[0][0], spine[0][1]);
    for (const [x, y] of spine.slice(1)) ctx.lineTo(x, y);
  };
  for (const [lx, far] of [[-34, true], [-18, false], [62, true], [78, false]] as const) {
    const [sx, sy] = spine[Math.round(((lx + 56) / 176) * 24)];
    noodle(ctx, [[sx, sy + 12], [sx + (far ? -6 : 8), sy + 27]], 6, far ? belly : skin, line);
  }
  trace();
  stroke(ctx, line, 48);
  trace();
  stroke(ctx, skin, 44);
  const tail = spine[spine.length - 1];
  ctx.beginPath();
  ctx.moveTo(tail[0] - 40, tail[1] - 14);
  ctx.quadraticCurveTo(tail[0] + 10, tail[1] - 26, tail[0] + 26, tail[1]);
  ctx.quadraticCurveTo(tail[0] + 10, tail[1] + 18, tail[0] - 40, tail[1] + 12);
  if (mono) fillOnly(ctx, "#000");
  else {
    ctx.fillStyle = "rgba(255,225,238,0.85)";
    ctx.fill();
    ctx.strokeStyle = line;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.save();
    ctx.translate(0, -8);
    trace();
    ctx.strokeStyle = "rgba(255,255,255,0.5)";
    ctx.lineWidth = 5;
    ctx.stroke();
    ctx.restore();
    for (let k = 3; k < 22; k += 4) {
      const [x, y] = spine[k];
      ctx.globalAlpha = 0.35;
      ell(ctx, x, y + 2, 5, 3);
      fillOnly(ctx, "#e8b8cc");
      ctx.globalAlpha = 1;
    }
  }
  const [hx0, hy0] = spine[0];
  ctx.save();
  ctx.translate(hx0, hy0);
  ctx.scale(1.3, 1.3);
  ctx.translate(-hx0, -hy0);
  const hx = hx0 - 22;
  const hy = hy0 - 6;
  for (let k = 0; k < 4; k++) {
    const sw = Math.sin(t * 2 + k) * 0.08;
    gill(ctx, MORPHS[0], "glow", hx + 20, hy - 10 + k * 7, -Math.PI / 2 - 0.35 + k * 0.5 + sw, 36 - k * 4, 4.6, t, k);
  }
  ctx.beginPath();
  ctx.moveTo(hx + 26, hy - 18);
  ctx.quadraticCurveTo(hx - 14, hy - 26, hx - 40, hy - 2);
  ctx.quadraticCurveTo(hx - 30, hy + 16, hx + 4, hy + 18);
  ctx.quadraticCurveTo(hx + 30, hy + 16, hx + 26, hy - 18);
  ink(ctx, skin, line, 3);
  highlight(ctx, hx - 10, hy - 14, 14, 4, -0.15);
  if (!mono) {
    glow(ctx, hx - 4, hy - 10, 20, "190,225,255", 0.55);
    ctx.beginPath();
    ctx.arc(hx - 4, hy - 10, 7.5, Math.PI * 0.35, Math.PI * 1.65);
    ctx.arc(hx - 0.5, hy - 10, 6, Math.PI * 1.55, Math.PI * 0.45, true);
    ctx.closePath();
    ink(ctx, "#d8ecff", "#6f96c8", 1.4);
  }
  const f: Face = face === "normal" ? "sleepy" : face;
  eyes(ctx, f, t, [hx - 20], hy - 2, 3.6, "#8a6a7a");
  mouth(ctx, face === "sleepy" ? "normal" : face, hx - 30, hy + 7, 4, line);
  blush(ctx, [hx - 12], hy + 5, 4.5, "#ffb3c8");
  ctx.restore();
}

// ---------- registry ----------

type ArtFn = (ctx: CanvasRenderingContext2D, t: number, face: Face, m: Morph) => void;

const ART: Record<string, { draw: ArtFn; morph?: Morph }> = {
  splashlotl: { draw: drawSplashlotl, morph: MORPHS[0] },
  tidelotl: { draw: drawTidelotl, morph: MORPHS[0] },
  emberlotl: { draw: drawEmberlotl, morph: MORPHS[2] },
  blazelotl: { draw: drawBlazelotl, morph: MORPHS[2] },
  sproutlotl: { draw: drawSproutlotl, morph: MORPHS[1] },
  grovelotl: { draw: drawGrovelotl, morph: MORPHS[1] },
  boinglotl: { draw: drawBoinglotl, morph: MORPHS[0] },
  bumblebun: { draw: drawBumblebun },
  glimmoth: { draw: drawGlimmoth },
  snailor: { draw: drawSnailor },
  froggle: { draw: drawFroggle },
  lilypadda: { draw: drawLilypadda },
  newtling: { draw: drawNewtling },
  cindermouse: { draw: drawCindermouse },
  batsy: { draw: drawBatsy },
  mushgloom: { draw: drawMushgloom },
  beetlight: { draw: drawBeetlight },
  drumbolt: { draw: drawDrumbolt },
  hookroc: { draw: drawHookroc },
  snoozicle: { draw: drawSnoozicle },
  reindoc: { draw: drawReindoc },
  olm: { draw: drawOlm },
};

export function hasArt(form: string) {
  return form in ART;
}

// (x, y) = the point on the ground under the Lippie; size = pixels per 100 units.
export function drawLippie(ctx: CanvasRenderingContext2D, form: string, x: number, y: number, size: number, opts: LippieOpts = {}) {
  const art = ART[form];
  if (!art) return;
  const t = opts.t ?? 0;
  const s = size / 100;
  const breathe = Math.sin(t * 2.4 + x * 0.01) * 0.015;
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(s * (opts.flip ? -1 : 1) * (1 - breathe * 0.5), s * (1 + breathe));
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  mono = opts.mono ?? null;
  try {
    art.draw(ctx, t, opts.face ?? "normal", opts.morph ?? art.morph ?? MORPHS[0]);
  } finally {
    mono = null;
    ctx.restore();
  }
}
